import React, { useState, useEffect, useContext, ReactNode } from "react";
import { useForm, FormProvider } from "react-hook-form";
import GlobalContext from "../../../contexts/globalContext";
import dayjs from 'dayjs';

// Components //
import FormHead from "../../molecules/form-head/FormHead";
import ErrorModal from "../../ui/modal/Modal";
import moment from "moment";
import { formMentoriaTalentoAdapter } from "./adapters/formMentoriaTalentoAdapter";
import { listCityAmva } from "./utils/listCityAmva";
import { listLevelStudy } from "./utils/listLevelStudy";
import { FormMentoriaTalentoModalSwitcher, ModalSuccesType } from "./FormMentoriaTalentoModalSwitcher";

import { FormMentoriaTalentoQuery } from "./queries/FormMentoriaTalentoQuery";
import { FormMentoriaTalentoSection } from "./FormMentoriaTalentoSection";
import {
    Step,
    StepLabel,
    Stepper,
    Button,
    Box,
    Grid,
    Container,
    Typography,
} from "@comfama/comfama-ui";
import rawToReact from "../../../utils/rawToReact";
import contentfulRenderOptions from "../../../utils/contentfulRenderOption";
import { saveForm, getToken } from "../../../services/formServices";
import Loading from "../../atoms/loading/loading";
import "./FormMentoriaTalento.scss"

interface IFormMentoriaTalentoValues{
   [key: string]: { contentfulId: string; answer: string };
} 

const AutoempleoLabel = "Autoempleo";
const UnemploymentLabel = "Desempleado";

const FormMentoriaTalento = () => {
    // Obtiene el contenido de un formulario de tipo Contactenos desde contentful
    const data = FormMentoriaTalentoQuery();
    const adapterData = formMentoriaTalentoAdapter(data);
    const contentfulData = data.form.nodes[0];
    // data modals
    const modalWithoutAge = data.modalWithoutAge.nodes[0];
    const modalSuccess = data.modalSuccess.nodes[0];
    const modalSuccessVariants = data.modalSuccessVariants.nodes[0];
    const modalUnsuccess = data.modalUnsuccess.nodes[0];

    const objForm = {};

    adapterData.sections.forEach((item) => {
        item.fields.forEach((subItem) => {
            objForm[subItem.name] = {
                contentfulId: subItem.name,
                answer: "",
            };
        });
    });

    const methods = useForm<IFormMentoriaTalentoValues>({
        defaultValues: {
            ...objForm,
            registerDate: {
                contentfulId: "registerDate",
                answer: moment(Date.now()).format("DD-MM-YYYY"),
            },
        },
    });
    
    const {
        handleSubmit,
        setValue,
        watch,
        trigger,
        getValues,
        reset,
        setError,
    } = methods;
    
    const [activeStep, setActiveStep] = useState<number>(0);
    // Estado para confirmar que el step esta completamente diligenciado.
    const [isStepValid, setIsStepValid] = useState<boolean>(false);
    //Estado para listado de opciones de modalidad mentoria
    const [modMentoryOptions, setModMentoryOptions] = useState<{ label: string; value: string }[]>([]); 
    const [isLoading, setIsLoading] = useState<boolean>(false);
    //Estados de modales exitoso, error y no cuenta con la edad
    const [isOpenModal, setIsOpenModal] = useState<boolean>(true);
    const [modalSuccesTypevalue, setModalSuccesType] = useState<ModalSuccesType>();
    const [showModalError, setShowModalError] = useState(false);
    const [showModalWithoutAge, setShowModalWithoutAge] = useState<boolean>(false);

    const [disabilityModal, setDisabilityModal] = useState<boolean>(false);

    interface IModalContent {
        title: string;
        image: string;
        textButton: string;
        url: string;
        body: ReactNode;
    }

    const [modalContent, setModalContent] = useState<IModalContent>();

    const Context = useContext(GlobalContext);

    // Guarda los valores de la cuenta de gigya que este logueada
    useEffect(() => {
        if (Context.gigyaContext.gigyaAccount) {
            setValue(
                "fullName.answer",
                `${Context.gigyaContext.gigyaAccount.profile.firstName} ${Context.gigyaContext.gigyaAccount.profile.lastName}`
            );
            setValue(
                "docNumber.answer",
                Context.gigyaContext.gigyaAccount.data.docNumber
            );
            setValue(
                "email.answer",
                Context.gigyaContext.gigyaAccount.profile.email
            );
            setValue(
                "mobile.answer",
                Context.gigyaContext.gigyaAccount.data.mobile
            );
        }
    }, [Context.gigyaContext.gigyaAccount]);

    // con base a los valores del formualrio, retorna el tipo de modal exitoso que se debe renderizar.
    const getModalSuccesType = (values: IFormMentoriaTalentoValues ): ModalSuccesType  => {

      const isAutoEmployed = values?.interesMentoria?.answer?.includes(AutoempleoLabel);
    
      if(isAutoEmployed) {
        return ModalSuccesType.selfEmployment;
      }

      const isAmva = listCityAmva.includes(values?.city?.answer);

      if(isAmva) {
        return ModalSuccesType.findJobAMVA;
      }

      return ModalSuccesType.finJobRegions;
    }

    // Guarda en un estado con el contenido de los modales que se renderizan al finalizar el formulario
    const handleModalContent = (status: number) => {
        if (status === 200) {

            const modalSuccesType = getModalSuccesType(getValues());

            let modalSuccessContent: IModalContent;

            if(modalSuccesType === ModalSuccesType.finJobRegions) { 
               modalSuccessContent = {
                  title: modalSuccess.tituloPrincipal,
                  image: modalSuccess.imagenFormulario.file.url,
                  textButton: modalSuccess.tituloBoton,
                  url: modalSuccess.redireccionBoton,
                  body: rawToReact(modalSuccess.descripcionFormulario.raw,contentfulRenderOptions),
               }
            } else {
               const isForAmva = modalSuccesType === ModalSuccesType.findJobAMVA;
               modalSuccessContent = {
                  title: modalSuccessVariants.tituloPrincipal,
                  image: modalSuccessVariants.imagenFormulario.file.url,
                  textButton: isForAmva ? modalSuccessVariants.tituloBotonOpcional : modalSuccessVariants.tituloBoton,
                  url: isForAmva ?  modalSuccessVariants.redireccionBotonOpcional :  modalSuccessVariants.redireccionBoton,
                  body: rawToReact(modalSuccessVariants.descripcionFormulario.raw,contentfulRenderOptions),
               }
            }
            setModalContent(modalSuccessContent);
            setModalSuccesType(modalSuccesType);
            dataLayerConfirmation();
        } else {
            setModalContent({
                title: modalUnsuccess.tituloPrincipal,
                image: modalUnsuccess.imagenFormulario.file.url,
                textButton: modalUnsuccess.tituloBoton,
                url: modalUnsuccess.redireccionBoton,
                body: rawToReact(modalUnsuccess.descripcionFormulario.raw,contentfulRenderOptions),
            });
        }
    };
    // Función para verificar si todos los campos visibles están dilingeciados //pendiente implementar useMemo() para performace
    const isStepComplete = () => {
        const fields = adapterData.sections[activeStep].fields;

        return fields
            .filter((field) => !field.disabled)
            .map((field) => field.name);
    };

    const value = watch(isStepComplete());

    //Validad activa el boton continuar si los campos estan diligenciados todos
    useEffect(() => {
        const fn = async () => {
            const validateTrigger = await trigger(isStepComplete());
            setIsStepValid(validateTrigger);
        };
        fn();
    }, [value, activeStep]);

    //Filtra las opciones de modalidad mentoría dependiendo de la ciudad seleccionada
    useEffect(() => {
        if (activeStep === 1) {
            const cityAnswer = getValues("city.answer");
            const studiesLevelAnswer = getValues("studiesLevel.answer");
            const statusDisabilityAnswer = getValues("statusDisability.answer");
            const disabilityAnswer = getValues("disability.answer");
            const interesMentoriaAnswer = getValues("interesMentoria.answer");
    
            const modMentoryField = adapterData.sections[1].fields.find(
                (field) => field.name === "modMentory"
            );
    
            if (modMentoryField && modMentoryField.options) {
                let filteredOptions = [];
                    //casos (AMVA y REG) se mantienen igual
                    if (listCityAmva.includes(cityAnswer)) {
                        filteredOptions = modMentoryField.options
                            .filter((option) => option.value.startsWith("AMVA--"))
                            .map((option) => {
                                const isVirtualPreRecorded = option.value.startsWith("AMVA--Mentoría virtual pregrabada");
                                // Si la opción es "Mentoría virtual pregrabada", validamos las condiciones
                                if (isVirtualPreRecorded) {
                                    const shouldHide = !listLevelStudy.includes(studiesLevelAnswer) 
                                        ? true 
                                        : (statusDisabilityAnswer === "SÍ" && disabilityAnswer !== "Física") 
                                        || (statusDisabilityAnswer === "NO" && interesMentoriaAnswer !== "Estoy interesado en encontrar o cambiar de empleo");
                                    if (shouldHide) {
                                        return null; // Ocultamos la opción
                                    }
                                }
                                return {
                                    ...option,
                                    value: option.value.replace("AMVA--", ""),
                                    label: option.label.replace("AMVA--", ""),
                                };
                            })
                            .filter(Boolean);
                    } else {
                        filteredOptions = modMentoryField.options
                            .filter((option) => option.value.startsWith("REG--"))
                            .map((option) => {
                                const isVirtualPreRecorded = option.value.startsWith("REG--Mentoría virtual pregrabada");
                                // Si la opción es "Mentoría virtual pregrabada", validamos las condiciones
                                if (isVirtualPreRecorded) {
                                    const shouldHide = !listLevelStudy.includes(studiesLevelAnswer) 
                                        ? true 
                                        : (statusDisabilityAnswer === "SÍ" && disabilityAnswer !== "Física") 
                                        || (statusDisabilityAnswer === "NO" && interesMentoriaAnswer !== "Estoy interesado en encontrar o cambiar de empleo");
                                    if (shouldHide) {
                                        return null; // Ocultamos la opción
                                    }
                                }
                                return {
                                    ...option,
                                    value: option.value.replace("REG--", ""),
                                    label: option.label.replace("REG--", ""),
                                };
                            })
                            .filter(Boolean);
                    }
                setModMentoryOptions(filteredOptions);
            }
        }
    }, [
        activeStep,
        watch("city.answer"),
        watch("studiesLevel.answer"),
        watch("statusDisability.answer"),
        watch("disability.answer"),
        watch("interesMentoria.answer"),
    ]);
    
    
    const disabilityState = watch("statusDisability.answer");
    const statusJobState = watch("statusJob.answer");

    const showUnderOathSection = statusJobState?.toLowerCase() === UnemploymentLabel?.toLowerCase();


    useEffect (()=>{
        if (disabilityState === "SÍ" && !listCityAmva.includes(getValues("city.answer"))){
            setDisabilityModal(true);
        } else {
            setDisabilityModal(false);
        }
    },[disabilityState])

    useEffect (()=>{
      setValue("jobCondition.answer" , showUnderOathSection ? "SÍ" : "");
    },[statusJobState])

    //Reinicia los campos de la seccion 2 cuando la respuesta de estado de discapacidad cambia
    useEffect(() => {
        const currentValues = getValues();
        reset({
            ...currentValues,
            jobCondition: {...currentValues.jobCondition , answer: "" },
            disability: { ...currentValues.disability , answer: "" },
            specialRequirement: { ...currentValues.specialRequirement, answer: ""},
            specialRequirementDetail: { ...currentValues.specialRequirementDetail, answer: ""},
            disabilityDetail: { ...currentValues.disabilityDetail, answer: "" },
            interesMentoria: { ...currentValues.interesMentoria, answer: "" },
            modMentory: { ...currentValues.modMentory, answer: "" },
        });
    }, [watch("statusDisability.answer")]);

    // Validacion input email- mobile
    const changeStepper = () => {

        const isBirthDateValid = validateBirthDate();

        if (!isBirthDateValid) {
            setShowModalWithoutAge(true);
            return;
        }
        const regexEmail = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i;
        const validationMobile = getValues("mobile.answer");
        const validationPhone = getValues("phone.answer");
        if (!regexEmail.test(getValues("email.answer"))){
            return setError("email", {type: "manual", message: "* El correo no es válido"});
        }
        if (validationMobile.length !== 10){
            return setError("mobile", {type: "manual", message: "* El número no es válido."})
        }
        if (validationPhone.length !== 10){
            return setError("phone", {type: "manual", message: "* El número no es válido."})
        }
        setActiveStep(activeStep + 1);
    };

    //Validación fecha de nacimiento con el botón siguente
    const validateBirthDate = () => {
        //Validación fecha de nacimiento
        const valueBirthDate = getValues("birthDate.answer");
        const isValidDate = dayjs(valueBirthDate, "DD-MM-YYYY", true).isValid();

        if (valueBirthDate && isValidDate) {
            const birthDate = dayjs(valueBirthDate, "DD-MM-YYYY");
            const today = dayjs();
    
            const age = today.diff(birthDate, 'year');
            // Validar que tenga al menos 16 años
            if (age < 16) {
                methods.resetField("birthDate.answer");
                return false;
            } else {
                setShowModalWithoutAge(false);
                return true;
            }
        }
    };

    const dataLayerConfirmation = () => {
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({
            'event': 'confirmacionEnvioExitosoFormulario',
            'envioformulario' : `empleo_confirmacion_envio_exitoso_formulario_mentoria`
        });
    }

    const onSubmit = async () => {
        setIsLoading(true);
        try {
            const formatDate = dayjs(getValues("birthDate.answer")).format("DD-MM-YYYY");
            setValue("birthDate.answer" , formatDate);
            const valuesForm = getValues();
            const formMentoria = { "idForm": 3, "answers" : Object.values(valuesForm)};
            const token = await getToken();
            const response = await saveForm(formMentoria, token);
            handleModalContent(response.data.status);
            setIsLoading(false);
            setIsOpenModal(true);
        } catch (error) {
            setShowModalError(true);
            setIsLoading(false);
        }
    };


    return (
        <Container maxWidth="md">
            <div
                // Asigna como nombre de la Clase el meta titulo del formulario
                className={`o-form-${contentfulData.metaTitulo}`}
            >
                <div className={`o-form-${contentfulData.metaTitulo}__cont`}>
                    <div
                        className={`o-form-${contentfulData.metaTitulo}__cont-form`}
                    >
                        <FormHead
                            descripcionFormulario={JSON.parse(
                                contentfulData.descripcionFormulario.raw
                            )}
                        />
                        {/* Renderiza el cuerpo del formulario */}
                        <FormProvider {...methods}>
                            <form onSubmit={handleSubmit(onSubmit)}>
                                <Stepper
                                    activeStep={activeStep}
                                    alternativeLabel
                                >
                                    {adapterData.sections.map((item) => (
                                        <Step key={item.title}>
                                            <StepLabel>{item.title}</StepLabel>
                                        </Step>
                                    ))}
                                </Stepper>
                                <Grid container item spacing={3}>
                                    {adapterData.sections.map((item, idx) => {
                                        return (
                                            activeStep == idx && (
                                                <FormMentoriaTalentoSection
                                                    key={item.title}
                                                    title={item.title}
                                                    fields={item.fields.map(
                                                        (field) =>
                                                            field.name ===
                                                            "modMentory"
                                                                ? {
                                                                      ...field,
                                                                      options:
                                                                          modMentoryOptions,
                                                                  }
                                                                : field
                                                    )}
                                                />
                                            )
                                        );
                                    })}
                                </Grid>
                                {(activeStep === 1 ) && (
                                    <>
                                        { disabilityModal ? 
                                            <div className="m-section-form m-section-form-datosPersonales m-section-form-bottom footer-container modality-disability info-modal">
                                                <p className="footer-text">Tu mentoría será individual, y de acuerdo a la disponibilidad, podrá ser presencial o virtual.</p>
                                            </div> 
                                            :
                                            <div className="m-section-form m-section-form-datosPersonales m-section-form-bottom footer-container modality info-modal">
                                                <div className="m-section-form__virtual">
                                                    <img src="https://images.ctfassets.net/203frbsx1z3n/7smSES1AclVuOCzk12tkpg/4c38b0b5f9173600b11f2a1602e63026/computadora__1_.svg"/>
                                                    <p className="footer-text">
                                                        Para la <span className="modal-bold">modalidad virtual</span> deberás contar con acceso a internet y a un dispositivo como computador o celular que te permita conectarte al espacio.
                                                    </p>
                                                </div>
                                                <div className="m-section-form__presencial">
                                                    <img src="https://images.ctfassets.net/203frbsx1z3n/2X5mCTar7xIsrCsF2L3Xn3/58b53a749c925d0ce7e59493695f9d3a/libro-alt__3_.svg"/>
                                                    <p className="footer-text">Para la <span className="modal-bold">modalidad presencial</span> deberás contar con el tiempo y recursos para desplazarte al lugar en el cual se llevará a cabo la mentoría de talento.</p>
                                                </div>
                                                <div className="m-section-form__virtualpregrabada">
                                                    <img src="https://images.ctfassets.net/203frbsx1z3n/5w7T4ePeG40wJxh1qjOZ1r/0bf498488469898f3077a5fd3f2922f5/icon_virtual_pregrabada.svg"/>
                                                    <p className="footer-text">Para la <span className="modal-bold">modalidad virtual pregrabada</span> tendrás disponible contenido grabado que podrás verlo a tu propio ritmo y una sesión virtual personalizada con un mentor. (Sujeto a disponibilidad)</p>
                                                </div>
                                            </div>
                                        }
                                    </>
                                )}
                                <Box pt={8} textAlign={"center"} marginBottom={"10px"}>
                                    <Button
                                        variant="contained"
                                        size="small"
                                        disabled={activeStep === 0}
                                        onClick={() =>
                                            setActiveStep(activeStep - 1)
                                        }
                                        sx={{ mr: 1 }}
                                    >
                                        Atrás
                                    </Button>
                                    <Button
                                        variant="contained"
                                        size="small"
                                        disabled={!isStepValid}
                                        onClick={
                                            activeStep ===
                                            adapterData.sections.length - 1
                                                ? handleSubmit(onSubmit)
                                                : () => changeStepper()
                                        }
                                        sx={{ ml: 1 }}
                                    >
                                        {activeStep ===
                                        adapterData.sections.length - 1
                                            ? "Enviar"
                                            : "Siguiente"}
                                    </Button>
                                </Box>
                            </form>
                        </FormProvider>
                    </div>
                </div>
                {isLoading && (
                    <div className="loading">
                        <Loading />
                    </div>
                )}
                {/* Renderiza el modal exitoso */}
                {
                  isOpenModal && modalContent && modalSuccesTypevalue && (
                     <FormMentoriaTalentoModalSwitcher
                        type={modalSuccesTypevalue}
                        modalContent={modalContent}
                        metaTitulo={contentfulData.metaTitulo}
                      />
                  )
                }
               
                {showModalError && (
                    <ErrorModal
                        showModal={showModalError}
                        onClose={() => setShowModalError(false)}
                    >
                        <div className={`m-modal-container-validationMpc`}>
                            <img
                                src={modalContent?.image}
                                className="o-verification-mpc-response__img"
                                alt="Success"
                            ></img>
                            <h3> {modalContent?.title} </h3>
                            <div className="m-modal-container-validationMpc__paragraph">
                                {modalContent?.body}
                            </div>
                        </div>
                    </ErrorModal>
            )}
                {showModalWithoutAge && (
                <ErrorModal
                    showModal={showModalWithoutAge}
                    onClose={() => setShowModalWithoutAge(false)}
                >
                    <Box className={`m-modal-container-validationMpc`}>
                        <img
                            src={modalWithoutAge.imagenFormulario.file.url}
                            className="o-verification-mpc-response__img"
                            alt="Success"
                        ></img>
                        <Typography variant="h3"> {modalWithoutAge.tituloPrincipal} </Typography>
                        <Box className="m-modal-container-validationMpc__paragraph">
                            <Typography variant="subtitle1" component="p">
                                {rawToReact(
                                    modalWithoutAge.descripcion.raw,
                                    contentfulRenderOptions
                                )}
                            </Typography >
                        </Box>
                        <Button
                            variant="contained"
                            onClick={() => {setShowModalWithoutAge(false)}}
                        >
                            {modalWithoutAge.tituloBoton}
                        </Button>
                            
                        
                    </Box>
                </ErrorModal>
            )}
            </div>
        </Container>
    );
};

export default FormMentoriaTalento;
